<script setup lang="ts">
import {
  EnumerationEvaluationParameterDto,
  EvaluationParameterDto,
  instanceOfEnumerationEvaluationParameterDto,
  instanceOfNumericEvaluationParameterDto,
} from '@/api/generator';
import { computed } from 'vue';
import { translateEntityProperty } from '@/utils/helper';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import TextField from '@/components/forms/TextField/TextField.vue';
import Textarea from '@/components/forms/Textarea/Textarea.vue';
import { getEvaluationTypeString, getLimitTypeString } from '@/views/TrendsDisplayView/utils';

const props = defineProps<{
  evaluations?: EvaluationParameterDto[];
}>();

const enabledEvaluations = computed<EvaluationParameterDto[] | undefined>(() =>
  props.evaluations?.filter((e) => e.isEnabled),
);

function getActiveOptions(enumeration: EnumerationEvaluationParameterDto) {
  return enumeration.options?.filter((option) => option.isEnabled);
}
</script>

<template>
  <v-sheet v-for="evaluation in enabledEvaluations" :key="evaluation.id" border class="pa-3 mb-4">
    <v-row>
      <v-col cols="3">
        <v-label v-if="instanceOfEnumerationEvaluationParameterDto(evaluation)">{{
          $t('Enumeration_one')
        }}</v-label>
        <v-label v-else>{{ $t('Quantity_one') }}</v-label>
        <div>
          <IdentifierLabel
            :label="`${translateEntityProperty('name', evaluation)} ${'geometry' in evaluation && evaluation.geometry ? translateEntityProperty('name', evaluation?.geometry) : ''}`"
          />

          <br />

          <v-label class="mt-4">{{ $t('Instruction_one') }}</v-label>
          <Textarea :modelValue="evaluation.instruction" mode="details" />
        </div>
      </v-col>

      <v-col cols="3">
        <div v-if="instanceOfNumericEvaluationParameterDto(evaluation)">
          <v-label>{{ $t('EvaluationType_one') }}</v-label>
          <div>
            {{ getEvaluationTypeString(evaluation, true) }}
          </div>
        </div>
      </v-col>

      <v-col cols="3">
        <div v-if="instanceOfNumericEvaluationParameterDto(evaluation)">
          <v-label>{{ $t('LimitTypeLabel') }}</v-label>
          <div>
            {{ getLimitTypeString(evaluation, true) }}
          </div>
        </div>
      </v-col>

      <v-col cols="3">
        <div class="executionPanel h-100 w-100">
          <div v-if="instanceOfNumericEvaluationParameterDto(evaluation)">
            <v-label class="mb-2">{{ $t('Value') }}</v-label>
            <TextField disabled :suffix="evaluation.evaluationUnitAbbreviation ?? undefined" />
          </div>
          <div v-if="instanceOfEnumerationEvaluationParameterDto(evaluation)">
            <v-label class="mb-2">{{ $t('ManualInput') }}</v-label>
            <div>
              <v-radio-group>
                <v-radio
                  v-for="option in getActiveOptions(evaluation)"
                  :key="option.id"
                  disabled
                  :label="option.name ?? ''"
                  :value="option"
                  class="align-start"
                />
              </v-radio-group>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-sheet>
</template>
